import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { EditableSectionHeading } from '../../edit/EditableSectionHeading';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveSectionLucideIcon } from '../../icons/ctaLucideMap';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { safeMapEmbedUrl } from '../../lib/safeGuestLinks';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { pickChrome } from '../../lib/siteChromeCopy';
import { parseHoursSchedule } from '../../lib/splitHoursLines';
import type { BlockRenderProps } from '../../types';

/** Wide stop board — copy + schedule + map (hours as bound day/time rows). */
export function StaticLocationStop({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIconKey = pickBlockField(block.content, 'sectionIcon', locale);
  const locationLabel = pickBlockField(block.content, 'locationLabel', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const directions = pickBlockField(block.content, 'directions', locale);
  const hours = pickBlockField(block.content, 'hours', locale);
  const hoursRows = parseHoursSchedule(hours);
  const mapEmbedUrl = safeMapEmbedUrl(pickBlockField(block.content, 'mapEmbedUrl', locale));
  const mapImage = pickMediaUrl(block, 'mapImage');
  const mapImageSrc = mapImage ? buildSiteImageProxyUrl(mapImage, 1200, 78) : undefined;
  const mapImageSrcSet = mapImage
    ? buildSiteImageSrcSet(mapImage, [480, 768, 1200], 78)
    : undefined;
  const target = isActive ? activeEditTarget : null;
  const sectionIcon = resolveSectionLucideIcon(sectionIconKey, 'location');
  const showMap = Boolean(mapEmbedUrl || mapImageSrc || editable);

  return (
    <section
      className={['ws-stop-card', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="location"
    >
      <div className="ws-stop-card__inner">
        <div className="ws-stop-card__copy">
          <header className="ws-stop-card__header">
            <EditableContent
              as="span"
              fieldKey="sectionIcon"
              editable={editable}
              activeEditTarget={target}
              className="ws-static-location__icon-hit ws-stop-card__icon-hit"
            >
              <LucideIcon icon={sectionIcon} size="md" className="ws-stop-card__icon" />
            </EditableContent>
            <div className="ws-stop-card__heading">
              <EditableContent
                as="h2"
                fieldKey="sectionTitle"
                editable={editable}
                activeEditTarget={target}
                className="ws-stop-card__title"
              >
                {sectionTitle}
              </EditableContent>
              {locationLabel || editable ? (
                <EditableContent
                  fieldKey="locationLabel"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-stop-card__label"
                >
                  {locationLabel}
                </EditableContent>
              ) : null}
            </div>
          </header>
          <EditableContent
            fieldKey="address"
            editable={editable}
            activeEditTarget={target}
            className="ws-stop-card__address"
          >
            {address}
          </EditableContent>
          {hoursRows.length > 0 || editable ? (
            <div className="ws-stop-card__hours-block">
              <EditableSectionHeading
                label={pickChrome('footerHours', locale)}
                fieldKey="hours"
                editable={editable}
                className="ws-stop-card__hours-heading"
              />
              {hoursRows.length > 0 ? (
                <EditableContent
                  as="div"
                  fieldKey="hours"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-stop-card__hours ws-hours-schedule"
                >
                  {hoursRows.map((row, index) => (
                    <div key={`${row.day}-${row.time}-${index}`} className="ws-hours-schedule__row">
                      <span className="ws-hours-schedule__day">{row.day}</span>
                      <span className="ws-hours-schedule__time">{row.time}</span>
                    </div>
                  ))}
                </EditableContent>
              ) : editable ? (
                <EditableContent
                  fieldKey="hours"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-stop-card__hours-editor"
                >
                  {hours}
                </EditableContent>
              ) : null}
            </div>
          ) : null}
          {directions || editable ? (
            <EditableContent
              fieldKey="directions"
              editable={editable}
              activeEditTarget={target}
              className="ws-stop-card__directions"
            >
              {directions}
            </EditableContent>
          ) : null}
        </div>
        {showMap ? (
          <div className="ws-stop-card__map">
            {mapEmbedUrl ? (
              <iframe
                className="ws-stop-card__embed"
                src={mapEmbedUrl}
                title={sectionTitle || 'Map'}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            ) : mapImageSrc ? (
              <EditableMedia
                fieldKey="mapImage"
                editable={editable}
                activeEditTarget={target}
                className="ws-stop-card__photo"
                src={mapImageSrc}
                srcSet={mapImageSrcSet}
                sizes={SITE_IMAGE_SIZES.menuBanner}
                alt={address || sectionTitle || 'Map'}
                width={1200}
                height={800}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <EditableMedia
                fieldKey="mapImage"
                editable={editable}
                activeEditTarget={target}
                className="ws-stop-card__photo"
                src=""
                alt={address || sectionTitle || 'Map'}
                width={1200}
                height={800}
              />
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}
